import { useCallback, useEffect, useRef, useState } from "react";
import { Fingerprint } from "lucide-react";
import { isNative } from "@/lib/appConfig";
import storage from "@/lib/storage";
import { biometricVerify } from "@/lib/biometric";
import Logo from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";

export const BIOMETRIC_LOCK_KEY = "qc_biometric_lock";
const LOCK_AFTER_MS = 60 * 1000; // re-lock if backgrounded longer than this

function lockEnabled() {
    return storage.getItem(BIOMETRIC_LOCK_KEY) === "1";
}

// Biometric app lock for native: prompts on launch and on resume after a timeout,
// and covers the screen while backgrounded (privacy in the app switcher). Opt-in
// via Settings; no-op on web or when disabled. Gates the UI only — the JWT session
// is untouched, so unlocking is instant.
export default function AppLock() {
    const [locked, setLocked] = useState(() => isNative() && lockEnabled());
    const [covered, setCovered] = useState(false);
    const bgAt = useRef(0);
    const unlocking = useRef(false);

    const tryUnlock = useCallback(async () => {
        if (unlocking.current) return;
        unlocking.current = true;
        const ok = await biometricVerify("Desbloquea QCapital");
        unlocking.current = false;
        if (ok) setLocked(false);
    }, []);

    useEffect(() => {
        if (locked) tryUnlock();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isNative()) return;
        let remove = () => {};
        (async () => {
            const { App } = await import("@capacitor/app");
            const handle = await App.addListener("appStateChange", ({ isActive }) => {
                if (!isActive) {
                    bgAt.current = Date.now();
                    if (lockEnabled()) setCovered(true);
                } else {
                    setCovered(false);
                    if (lockEnabled() && bgAt.current && Date.now() - bgAt.current > LOCK_AFTER_MS) {
                        setLocked(true);
                        tryUnlock();
                    }
                }
            });
            remove = () => handle.remove();
        })();
        return () => remove();
    }, [tryUnlock]);

    if (!isNative()) return null;

    if (locked) {
        return (
            <div className="fixed inset-0 z-[100] bg-bone flex flex-col items-center justify-center gap-8 px-8" data-testid="app-lock">
                <Logo size="lg" />
                <Button
                    onClick={tryUnlock}
                    className="bg-petrol hover:bg-petrol-hover text-bone rounded-xl px-6 py-6"
                    data-testid="app-lock-unlock"
                >
                    <Fingerprint className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    Desbloquear
                </Button>
                <p className="text-soft_muted text-sm">QCapital esta bloqueado por seguridad.</p>
            </div>
        );
    }

    if (covered) {
        return (
            <div className="fixed inset-0 z-[100] bg-bone flex items-center justify-center" data-testid="app-cover">
                <Logo size="lg" />
            </div>
        );
    }

    return null;
}
